import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';

import { en } from './en';
import { zhCN } from './zh-CN';
import {
  LOCALES,
  type Locale,
  type LocaleDictionary,
  type PluralLeaf,
  type TranslationKey,
} from './types';

const DICTIONARIES: Record<Locale, LocaleDictionary> = { en, 'zh-CN': zhCN };

export type TranslateParams = Record<string, string | number> & { count?: number };

function lookup(dictionary: LocaleDictionary, key: string): unknown {
  return key.split('.').reduce<unknown>((node, part) => {
    if (node && typeof node === 'object' && part in node) {
      return (node as Record<string, unknown>)[part];
    }
    return undefined;
  }, dictionary);
}

function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

export function createTranslator(locale: Locale) {
  const dictionary = DICTIONARIES[locale];
  const pluralRules = new Intl.PluralRules(locale);

  return function translate(key: TranslationKey, params?: TranslateParams): string {
    let value = lookup(dictionary, key);

    // Fall back to English rather than showing a raw key. A half-translated
    // screen is recoverable; `dashboard.availableToReimburse` on screen is not.
    if (value === undefined && locale !== 'en') value = lookup(en, key);
    if (value === undefined) return key;

    if (typeof value === 'object' && value !== null && 'other' in value) {
      const plural = value as PluralLeaf;
      const category = pluralRules.select(params?.count ?? 0);
      const chosen = category === 'one' ? plural.one : plural.other;
      return interpolate(chosen, params);
    }

    return typeof value === 'string' ? interpolate(value, params) : key;
  };
}

// --- Formatting -------------------------------------------------------------

/**
 * Formatter instances are expensive to build and are rebuilt on every render
 * without a cache — noticeable on a list of a few hundred amounts.
 */
const formatterCache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();

function cached<T extends Intl.NumberFormat | Intl.DateTimeFormat>(key: string, build: () => T): T {
  const hit = formatterCache.get(key);
  if (hit) return hit as T;
  const made = build();
  formatterCache.set(key, made);
  return made;
}

export type MoneyOptions = {
  /** Drop the decimals — right for headline figures and chart axes. */
  round?: boolean;
  /** `1.2万` in Chinese, `$12K` in English. For axis ticks, never for totals. */
  compact?: boolean;
  showSign?: boolean;
};

export function formatMoney(
  cents: number,
  currency: string,
  locale: Locale,
  options: MoneyOptions = {},
): string {
  const key = `money:${locale}:${currency}:${options.round ? 1 : 0}:${options.compact ? 1 : 0}:${options.showSign ? 1 : 0}`;
  const formatter = cached(
    key,
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        notation: options.compact ? 'compact' : 'standard',
        maximumFractionDigits: options.compact ? 1 : options.round ? 0 : 2,
        minimumFractionDigits: options.compact || options.round ? 0 : 2,
        signDisplay: options.showSign ? 'exceptZero' : 'auto',
      }),
  ) as Intl.NumberFormat;

  return formatter.format(cents / 100);
}

export function formatNumber(value: number, locale: Locale, compact = false): string {
  const formatter = cached(
    `num:${locale}:${compact ? 1 : 0}`,
    () =>
      new Intl.NumberFormat(locale, {
        notation: compact ? 'compact' : 'standard',
        maximumFractionDigits: compact ? 1 : 0,
      }),
  ) as Intl.NumberFormat;
  return formatter.format(value);
}

export type DateStyle = 'full' | 'medium' | 'short' | 'monthYear' | 'month' | 'year';

/**
 * Calendar dates are parsed as UTC on purpose. `2026-03-14` is the day printed
 * on the receipt; letting the browser shift it into local time turns it into
 * the 13th for anyone west of Greenwich.
 */
export function formatDate(value: string, locale: Locale, style: DateStyle = 'medium'): string {
  if (!value) return '';
  const iso = value.length === 10 ? `${value}T00:00:00Z` : value;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return value;

  const options: Intl.DateTimeFormatOptions =
    style === 'full'
      ? { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }
      : style === 'short'
        ? { month: 'numeric', day: 'numeric', timeZone: 'UTC' }
        : style === 'monthYear'
          ? { year: 'numeric', month: 'short', timeZone: 'UTC' }
          : style === 'month'
            ? { month: 'short', timeZone: 'UTC' }
            : style === 'year'
              ? { year: 'numeric', timeZone: 'UTC' }
              : { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' };

  const formatter = cached(
    `date:${locale}:${style}`,
    () => new Intl.DateTimeFormat(locale, options),
  ) as Intl.DateTimeFormat;

  return formatter.format(date);
}

export function formatDateTime(value: string, locale: Locale): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const formatter = cached(
    `datetime:${locale}`,
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }),
  ) as Intl.DateTimeFormat;
  return formatter.format(date);
}

/** "3 days ago" / "3 天前", falling back to an absolute date past a month. */
export function formatRelative(value: string, locale: Locale): string {
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return value;

  const diffMs = date.getTime() - Date.now();
  const diffDays = Math.round(diffMs / 86_400_000);
  if (Math.abs(diffDays) > 30) return formatDate(value, locale, 'medium');

  const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (Math.abs(diffDays) >= 1) return relative.format(diffDays, 'day');

  const diffHours = Math.round(diffMs / 3_600_000);
  if (Math.abs(diffHours) >= 1) return relative.format(diffHours, 'hour');

  return relative.format(Math.round(diffMs / 60_000), 'minute');
}

// --- Context ----------------------------------------------------------------

type I18nValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: ReturnType<typeof createTranslator>;
  currency: string;
  money: (cents: number, options?: MoneyOptions) => string;
  date: (value: string, style?: DateStyle) => string;
  dateTime: (value: string) => string;
  relative: (value: string) => string;
  number: (value: number, compact?: boolean) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({
  locale,
  currency,
  onLocaleChange,
  children,
}: {
  locale: Locale;
  currency: string;
  onLocaleChange: (locale: Locale) => void;
  children: ReactNode;
}) {
  // `lang` drives font selection, line-height rules and screen-reader voice.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useMemo(() => createTranslator(locale), [locale]);

  const money = useCallback(
    (cents: number, options?: MoneyOptions) => formatMoney(cents, currency, locale, options),
    [currency, locale],
  );
  const date = useCallback(
    (value: string, style?: DateStyle) => formatDate(value, locale, style),
    [locale],
  );
  const dateTime = useCallback((value: string) => formatDateTime(value, locale), [locale]);
  const relative = useCallback((value: string) => formatRelative(value, locale), [locale]);
  const number = useCallback(
    (value: number, compact?: boolean) => formatNumber(value, locale, compact),
    [locale],
  );

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale: onLocaleChange,
      t,
      currency,
      money,
      date,
      dateTime,
      relative,
      number,
    }),
    [locale, onLocaleChange, t, currency, money, date, dateTime, relative, number],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside <I18nProvider>');
  return value;
}

export { LOCALES, type Locale, type TranslationKey };
